
import { useState } from 'react';
import Header from '../../components/feature/Header';

const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = [
    'All',
    'Publics',
    'Private Equity',
    'Crypto',
    'Funds',
    'Real Estate'
  ];

  const companies = [
    {
      name: 'Tesla Inc.',
      category: 'Publics',
      description: 'Electric vehicles and clean energy',
      status: 'Public',
      founded: 2003,
      website: '#'
    },
    {
      name: 'NVIDIA Corp.',
      category: 'Publics',
      description: 'Graphics processing and AI computing',
      status: 'Public',
      founded: 1993,
      website: '#'
    },
    {
      name: 'Microsoft Corp.',
      category: 'Publics',
      description: 'Software and cloud computing services',
      status: 'Public',
      founded: 1975,
      website: '#'
    },
    {
      name: 'Palantir Technologies',
      category: 'Publics',
      description: 'Data analytics and AI platforms',
      status: 'Public',
      founded: 2003,
      website: '#'
    },
    {
      name: 'Robinhood',
      category: 'Publics',
      description: 'Commission-free trading platform',
      status: 'Public',
      founded: 2013,
      website: '#'
    },
    {
      name: 'TSMC',
      category: 'Publics',
      description: 'Semiconductor manufacturing',
      status: 'Public',
      founded: 1987,
      website: '#'
    },
    {
      name: 'Berkshire Hathaway',
      category: 'Publics',
      description: 'Diversified holding company',
      status: 'Public',
      founded: 1839,
      website: '#'
    },
    {
      name: 'SpaceX',
      category: 'Private Equity',
      description: 'Space exploration and satellite technology',
      status: 'Private',
      founded: 2002,
      website: '#'
    },
    {
      name: 'Bitcoin',
      category: 'Crypto',
      description: 'Digital currency and store of value',
      status: 'Active',
      founded: 2009,
      website: '#'
    },
    {
      name: 'Ethereum',
      category: 'Crypto',
      description: 'Blockchain platform for smart contracts',
      status: 'Active',
      founded: 2015,
      website: '#'
    },
    {
      name: 'Coinbase',
      category: 'Crypto',
      description: 'Cryptocurrency exchange platform',
      status: 'Public',
      founded: 2012,
      website: '#'
    },
    {
      name: 'Binance',
      category: 'Crypto',
      description: 'Global cryptocurrency exchange',
      status: 'Private',
      founded: 2017,
      website: '#'
    },
    {
      name: 'Andreessen Horowitz',
      category: 'Funds',
      description: 'Venture capital firm',
      status: 'Fund',
      founded: 2009,
      website: '#'
    },
    {
      name: 'Morgan Stanley',
      category: 'Funds',
      description: 'Global investment banking and wealth management',
      status: 'Fund',
      founded: 1935,
      website: '#'
    },
    {
      name: 'Makena Capital',
      category: 'Funds',
      description: 'Alternative investment management firm',
      status: 'Fund',
      founded: 2005,
      website: '#'
    }
  ];

  const filteredCompanies = selectedCategory === 'All' 
    ? companies 
    : companies.filter(company => company.category === selectedCategory);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Public':
        return 'text-green-600';
      case 'REIT':
        return 'text-blue-600';
      case 'Fund':
        return 'text-purple-600';
      case 'Active':
        return 'text-orange-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <div className="pt-32 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-light text-black mb-8 tracking-tight">
            Portfolio
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl leading-relaxed">
            We back founders building the future. Our portfolio companies are solving humanity's most important problems.
          </p>
        </div>
      </div>

      {/* Filter Categories */}
      <div className="px-6 mb-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-black text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Companies Grid */}
      <div className="px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCompanies.map((company, index) => (
              <div
                key={index}
                className="group cursor-pointer"
              >
                <div className="border-b border-gray-200 pb-6 group-hover:border-black transition-colors duration-200">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-medium text-black group-hover:text-gray-600 transition-colors duration-200">
                      {company.name}
                    </h3>
                    <span className={`text-sm font-medium ${getStatusColor(company.status)}`}>
                      {company.status}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-3 leading-relaxed">
                    {company.description}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{company.category}</span>
                    <span>Founded {company.founded}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="border-t border-gray-200 pt-16">
            <div>
              <h2 className="text-3xl font-light text-black mb-6">
                Investment Philosophy
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We invest in companies that we believe will define the future. Our approach is to back exceptional founders who are building transformative technologies.
              </p>
              <p className="text-gray-600 leading-relaxed">
                From artificial intelligence to space exploration, from biotechnology to financial services, we support entrepreneurs who are solving humanity's most pressing challenges.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Portfolio;
